import apiClient from '@/lib/apiClient';

export type AssignmentMode = 'ASSIGN_TO_USER' | 'MATCH_OR_CREATE';
export type DetectedType = 'ROOM_AD' | 'ROOM_WANTED_AD';
export type Gender = 'MALE' | 'FEMALE' | 'UNKNOWN';
export type Smoking = 'SMOKER' | 'NON_SMOKER' | 'UNKNOWN';
export type RoommateGender = 'MALE' | 'FEMALE' | 'ANY' | 'UNKNOWN';
export type UserAssignment =
  | 'ASSIGNED_TO_OWNER'
  | 'MATCHED_BY_PHONE'
  | 'CREATED_FROM_PROFILE'
  | 'UNRESOLVED';

export interface AiAdExtraction {
  detectedType: DetectedType;
  confidence: number | null;
  personName?: string | null;
  phoneNumber?: string | null;
  phonePrefix?: string | null;
  phoneLocal?: string | null;
  phoneCountryCode?: string | null;
  hasWhatsapp?: boolean | null;
  title?: string | null;
  description?: string | null;
  cityName?: string | null;
  cityId?: number | null;
  districtName?: string | null;
  districtId?: number | null;
  nationality?: string | null;
  nationalityId?: number | null;
  gender?: Gender | null;
  age?: number | null;
  occupation?: string | null;
  smokingPreference?: Smoking | null;
  cleanliness?: string | null;
  lifestyleTraits?: string[];
  budgetMin?: number | null;
  budgetMax?: number | null;
  price?: number | null;
  deposit?: number | null;
  currency?: string | null;
  propertyType?: string | null;
  numberOfBedrooms?: number | null;
  numberOfBathrooms?: number | null;
  roomType?: string | null;
  rentType?: string | null;
  billType?: string | null;
  propertyFurnishing?: string | null;
  preferredRoommateGender?: RoommateGender | null;
  notes?: string | null;
  missingFields?: string[];
  warnings?: string[];
  changeRequests?: Array<{
    fieldPath?: string | null;
    label?: string | null;
    value?: string | null;
    confidence?: number | null;
    reason?: string | null;
  }>;
}

export interface AnalyzeResponse {
  extraction: AiAdExtraction;
  engine: 'openai' | 'fallback';
  model: string | null;
  inputChars: number;
}

export interface CreateRequestPayload {
  extraction: AiAdExtraction;
  finalType: DetectedType;
  assignmentMode: AssignmentMode;
  ownerUserId?: number | null;
  status?: string;
  featured?: boolean;
  showPhone?: boolean;
  showWhatsApp?: boolean;
  showOwnerInfo?: boolean;
  showInAppMessaging?: boolean;
  hideLocation?: boolean;
}

export interface CreateResponse {
  createdId: number;
  createdType: 'ROOM_AD' | 'ROOM_WANTED_AD';
  assignedUserId: number | null;
  userAssignment: UserAssignment;
  warnings: string[];
}

export type WhatsAppSuggestionStatus =
  | 'PENDING' | 'APPLIED' | 'IGNORED' | 'UNMATCHED' | 'NEEDS_REVIEW' | 'NEEDS_REPLY';
export type WhatsAppSuggestionTargetType = 'ROOM_AD' | 'ROOM_WANTED_AD' | 'UNMATCHED';
export type WhatsAppIntent =
  | 'AD_UPDATE' | 'ROOM_WANTED' | 'GREETING_OR_SMALL_TALK' | 'QUESTION'
  | 'REJECTION_OR_CLOSED' | 'AUTO_REPLY' | 'MEDIA_ONLY' | 'IRRELEVANT';

export interface WhatsAppUpdateSuggestion {
  id: number;
  sendPulseMessageId?: string | null;
  phoneE164?: string | null;
  phoneLocal?: string | null;
  incomingText?: string | null;
  targetType: WhatsAppSuggestionTargetType;
  targetAdId?: number | null;
  status: WhatsAppSuggestionStatus;
  intent?: WhatsAppIntent | null;
  ignoredReason?: string | null;
  extractedFields?: Record<string, unknown> | null;
  mediaUrls?: string[] | null;
  confidence?: number | null;
  proposedExtraction?: AiAdExtraction | null;
  changePreview?: Record<string, WhatsAppFieldChange>;
  processingNote?: string | null;
  appliedAt?: string | null;
  ignoredAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface WhatsAppFieldChange {
  key: string;
  label: string;
  beforeValue?: unknown;
  afterValue?: unknown;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export const aiAdApi = {
  analyze: async (text: string, language?: string, source?: string): Promise<AnalyzeResponse> => {
    const res = await apiClient.post('/admin/ai-ads/analyze', { text, language, source }, { timeout: 60000 });
    return res.data?.data;
  },

  create: async (body: CreateRequestPayload): Promise<CreateResponse> => {
    const res = await apiClient.post('/admin/ai-ads/create', body);
    return res.data?.data;
  },

  /**
   * Upload images to a freshly created ad. Routes to the right admin image
   * endpoint based on the created ad type — both ROOM_AD and ROOM_WANTED_AD
   * are supported with the same multipart contract.
   */
  uploadAdImages: async (
    createdType: 'ROOM_AD' | 'ROOM_WANTED_AD',
    adId: number,
    files: File[],
  ): Promise<void> => {
    if (!files.length) return;
    const base = createdType === 'ROOM_AD' ? 'room-ads' : 'room-wanted-ads';
    const fd = new FormData();
    files.forEach((f) => fd.append('images', f, f.name));
    await apiClient.post(`/admin/${base}/${adId}/images`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  listWhatsAppUpdateSuggestions: async (options?: {
    status?: WhatsAppSuggestionStatus;
    targetType?: WhatsAppSuggestionTargetType;
    targetAdId?: number | string | null;
    page?: number;
    size?: number;
  }): Promise<PageResponse<WhatsAppUpdateSuggestion>> => {
    const res = await apiClient.get('/admin/ai-ads/whatsapp-update-suggestions', {
      params: {
        status: options?.status ?? 'PENDING',
        targetType: options?.targetType,
        targetAdId: options?.targetAdId,
        page: options?.page ?? 0,
        size: options?.size ?? 20,
      },
    });
    return res.data?.data;
  },

  applyWhatsAppUpdateSuggestion: async (id: number): Promise<WhatsAppUpdateSuggestion> => {
    const res = await apiClient.post(`/admin/ai-ads/whatsapp-update-suggestions/${id}/apply`);
    return res.data?.data;
  },

  ignoreWhatsAppUpdateSuggestion: async (id: number): Promise<WhatsAppUpdateSuggestion> => {
    const res = await apiClient.post(`/admin/ai-ads/whatsapp-update-suggestions/${id}/ignore`);
    return res.data?.data;
  },
};
